import React from 'react'

import { Segment } from '../types'
import { findSegmentFromCursor } from '../utils/selectionUtils'

import { UseEditorStateReturn } from './useEditorState'

export interface UseNavigationProps extends UseEditorStateReturn {
  showPopupForSegment: (segment: Segment, node: HTMLElement) => void
  setIsMentionPopupOpen: (isOpen: boolean) => void
}

export const useNavigation = (props: UseNavigationProps) => {
  const {
    segments,
    elRefs,
    globalCursorPos,
    setGlobalCursorPos,
    setIsNavigating,
    isNavigating,
    showPopupForSegment,
    setIsMentionPopupOpen,
    setActiveIndex,
    nextCursorPos,
  } = props
  const editModeTimerRef = React.useRef<NodeJS.Timeout | null>(null)

  const cancelSwitchToEditMode = React.useCallback(() => {
    if (editModeTimerRef.current) {
      clearTimeout(editModeTimerRef.current)
      editModeTimerRef.current = null
    }
  }, [])

  const switchToEditModeAfterDelay = React.useCallback(
    (position: number) => {
      cancelSwitchToEditMode()
      editModeTimerRef.current = setTimeout(() => {
        const selection = window.getSelection()
        // Only switch to edit mode if there is no active selection range
        if (selection && selection.isCollapsed) {
          const result = findSegmentFromCursor(position, segments)
          if (result) {
            const { segment, localPos } = result
            const newIndex = segments.findIndex((s) => s.id === segment.id)

            if (newIndex !== -1) {
              setActiveIndex(newIndex)
              nextCursorPos.current = localPos
              setIsNavigating(false)
            }
          }
        }
      }, 20) // 20ms delay for switching to edit mode
    },
    [
      cancelSwitchToEditMode,
      setIsNavigating,
      segments,
      setActiveIndex,
      nextCursorPos,
    ],
  )

  const handleNavigationKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isNavigating) return false

    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      // For any other key, we cancel the pending switch to edit mode if there is one,
      // but we don't handle the event. This allows other handlers (like for typing) to take over.
      cancelSwitchToEditMode()
      return false
    }
    e.preventDefault()

    let newPos = globalCursorPos
    const totalLength = e.currentTarget.textContent?.length || 0

    if (e.key === 'ArrowLeft') newPos = Math.max(0, globalCursorPos - 1)
    if (e.key === 'ArrowRight') newPos = Math.min(totalLength, globalCursorPos + 1)
    if (e.key === 'ArrowUp') newPos = 0
    if (e.key === 'ArrowDown') newPos = totalLength

    setGlobalCursorPos(newPos)
    switchToEditModeAfterDelay(newPos) // Start the timer on navigation

    const result = findSegmentFromCursor(newPos, segments)
    if (result) {
      const { segment } = result
      const node = elRefs.current.get(segment.id)
      if (node) {
        showPopupForSegment(segment, node)
      }
    } else {
      setIsMentionPopupOpen(false)
    }

    return true
  }

  return {
    handleNavigationKeyDown,
    switchToEditModeAfterDelay,
    cancelSwitchToEditMode,
  }
} 